import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import SharedFooter from "@/components/SharedFooter";
import {
  ArrowRight,
  CheckCircle2,
  Users,
  Calendar,
  Video,
  Heart,
  Sparkles,
  Target,
  BookOpen,
  Lightbulb,
  MessageCircle,
  Star,
  Play,
  Globe,
  Accessibility,
  BarChart3,
  Zap,
  Clock,
  CalendarPlus,
  ExternalLink,
  Share,
  ChevronRight,
  Brain,
  Eye,
  Layers,
  Check
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InstructorPilotPage() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    document.title = "phygital - Instructor Pilot | Community Events";
  }, []);

  // Framework steps
  const frameworkSteps = [
    { id: "discover", label: "DISCOVER", icon: Globe, color: "bg-violet-500" },
    { id: "rsvp", label: "RSVP", icon: Calendar, color: "bg-blue-500" },
    { id: "intake", label: "INSIGHT INTAKE", icon: Heart, color: "bg-pink-500" },
    { id: "attend", label: "ATTEND", icon: Video, color: "bg-emerald-500" },
    { id: "insights", label: "HOST INSIGHTS", icon: BarChart3, color: "bg-amber-500" }
  ];

  // Insight categories
  const insightCategories = [
    {
      title: "Audience Types",
      icon: Users,
      color: "text-violet-600",
      bgColor: "bg-violet-50",
      items: ["Student", "Founder", "Creator", "Developer", "Designer", "Operator", "Curious Learner"]
    },
    {
      title: "Interests",
      icon: Heart,
      color: "text-pink-600",
      bgColor: "bg-pink-50",
      items: ["AI Tools", "Creative Technology", "Community Building", "Product Building", "Brand Strategy", "Digital Culture"]
    },
    {
      title: "Accessibility",
      icon: Accessibility,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      items: ["Captions Preferred", "Recording Access", "Camera Optional", "Low Bandwidth", "Visual Slides"]
    }
  ];

  // Benefits
  const benefits = [
    {
      icon: Eye,
      title: "Know Your Audience",
      description: "Collect insights before the event so you can tailor content and create meaningful experiences."
    },
    {
      icon: Accessibility,
      title: "Inclusive by Design",
      description: "Built-in accessibility framework supports both visible and invisible needs."
    },
    {
      icon: BarChart3,
      title: "Host Insights Dashboard",
      description: "Aggregated data helps you understand your community and improve future programming."
    },
    {
      icon: Zap,
      title: "Seamless Experience",
      description: "From RSVP to confirmation with Zoom link, calendar integration, and resources - all in one flow."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
              <Users className="w-3 h-3 mr-1" />
              Instructor Pilot
            </Badge>
            <Link to="/demo/communities">
              <Button className="bg-violet-600 hover:bg-violet-700 text-white">
                View Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-violet-100 text-violet-700 border-violet-200">
            FOR INSTRUCTORS & COMMUNITY HOSTS
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Host Events That<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">
              Understand Your Audience
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            A community operating system for events and instructors. Collect audience insights, 
            support accessibility needs, and build recurring programming - for IRL and URL events.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/demo/communities">
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white px-8 h-14 text-lg">
                See the Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-gray-300 h-14 text-lg">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Community Example: Phygital Community */}
      <section className="py-16 px-4 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-violet-600 font-medium mb-2">COMMUNITY EXAMPLE</p>
            <h2 className="text-3xl font-bold text-gray-900">The Phygital Community Build</h2>
          </div>
          
          <Card className="border-2 border-violet-100 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl">
                  🌙
                </div>
                <div className="text-white">
                  <h3 className="text-2xl font-bold">Phygital Community</h3>
                  <p className="text-white/80">Where creativity meets analytics</p>
                </div>
              </div>
            </div>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">About the Community</h4>
                  <p className="text-gray-600 leading-relaxed">
                    A community for creators, founders, and curious learners exploring the intersection 
                    of data, design, and digital culture. Hosting workshops, experiences, and building 
                    tools for the creator economy.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Featured Event</h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        <Video className="w-3 h-3 mr-1" />
                        URL Event
                      </Badge>
                    </div>
                    <h5 className="font-semibold text-gray-900">Front Row</h5>
                    <p className="text-gray-600 text-sm">Front Row Seat to Building a Digital Community</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* User Flow Framework */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-violet-600 font-medium mb-2">THE FLOW</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">From Discovery to Insights</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A complete user journey that collects valuable insights while creating a seamless experience for attendees.
            </p>
          </div>

          {/* Flow Steps */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {frameworkSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveStep(idx)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                      activeStep === idx
                        ? `${step.color} text-white shadow-lg`
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {step.label}
                  </button>
                  {idx < frameworkSteps.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-gray-300" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <Card className="border-2 border-gray-100">
            <CardContent className="p-8">
              {activeStep === 0 && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-8 h-8 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Discover Event</h3>
                  <p className="text-gray-600 max-w-lg mx-auto">
                    Users find your event through the community page. They see event details, 
                    date/time, event type (IRL or URL), and a preview of the instructor.
                  </p>
                </div>
              )}
              {activeStep === 1 && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">RSVP</h3>
                  <p className="text-gray-600 max-w-lg mx-auto">
                    Simple signup with name and email. Users can optionally create a profile 
                    that appears on the event page and community page.
                  </p>
                </div>
              )}
              {activeStep === 2 && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-pink-100 flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Insight Intake</h3>
                  <p className="text-gray-600 max-w-lg mx-auto mb-6">
                    Collect audience type, interests, and accessibility preferences. 
                    This data helps hosts tailor content and support all attendees.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                    {insightCategories.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <div key={cat.title} className={`${cat.bgColor} rounded-xl p-4 text-left`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${cat.color}`} />
                            <span className={`font-medium text-sm ${cat.color}`}>{cat.title}</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {cat.items.slice(0, 4).map((item) => (
                              <span key={item} className="text-xs bg-white/60 px-2 py-1 rounded-full text-gray-600">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeStep === 3 && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Video className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Attend Event</h3>
                  <p className="text-gray-600 max-w-lg mx-auto mb-4">
                    Confirmation page includes everything needed: Zoom link, calendar integration, 
                    event itinerary, resources, and Meet Your Instructor section.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                      <Video className="w-3 h-3 mr-1" /> Zoom Link
                    </Badge>
                    <Badge className="bg-green-50 text-green-700 border-green-200">
                      <CalendarPlus className="w-3 h-3 mr-1" /> Add to Calendar
                    </Badge>
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                      <BookOpen className="w-3 h-3 mr-1" /> Resources
                    </Badge>
                    <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                      <Star className="w-3 h-3 mr-1" /> Meet Instructor
                    </Badge>
                  </div>
                </div>
              )}
              {activeStep === 4 && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Host Insights</h3>
                  <p className="text-gray-600 max-w-lg mx-auto mb-4">
                    Community hosts see aggregated insights to understand their audience, 
                    improve future events, and build recurring programming.
                  </p>
                  <div className="grid grid-cols-3 gap-4 max-w-md mx-auto text-left">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-2xl font-bold text-gray-900">45%</p>
                      <p className="text-sm text-gray-500">Founders</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-2xl font-bold text-gray-900">35%</p>
                      <p className="text-sm text-gray-500">Captions</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-2xl font-bold text-gray-900">60%</p>
                      <p className="text-sm text-gray-500">AI Tools</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Meet Your Instructor */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-violet-600 font-medium mb-2">INSTRUCTOR SPOTLIGHT</p>
            <h2 className="text-3xl font-bold text-gray-900">Meet Your Instructor</h2>
          </div>

          <Card className="border-2 border-violet-100 overflow-hidden">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-5xl font-bold text-white flex-shrink-0 shadow-xl">
                  N
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900">Naomi</h3>
                  <p className="text-violet-600 font-medium mb-3">Founder of Phygital</p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Naomi is a community architect who helps creators and founders build meaningful 
                    digital spaces. She believes in the power of combining creativity with data-driven insights.
                  </p>
                  <p className="text-gray-900 font-medium">
                    Session Focus: <span className="text-violet-600">Building creative and analytical communities</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-violet-600 font-medium mb-2">WHY THIS SYSTEM</p>
            <h2 className="text-3xl font-bold text-gray-900">Built for Community Growth</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="border-2 border-gray-100 hover:border-violet-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-violet-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-violet-600 font-medium mb-2">FLEXIBLE FORMAT</p>
            <h2 className="text-3xl font-bold text-gray-900">IRL & URL Events</h2>
            <p className="text-gray-600 mt-2">Same system, different defaults</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-2 border-blue-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Video className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">URL</Badge>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Online Events</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Workshops, webinars, and virtual experiences with Zoom integration, 
                  recording access, and digital resource delivery.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Zoom Link</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Recording</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Low Bandwidth</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">IRL</Badge>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">In-Person Events</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Meetups, workshops, and experiences with venue details, 
                  accessibility accommodations, and dietary preferences.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Wheelchair Access</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Dietary Needs</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">Quiet Space</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Build Your Community?
          </h2>
          <p className="text-gray-600 mb-8">
            See the full user flow in action with our live demo featuring Phygital Community and the Front Row experience.
          </p>
          <Link to="/demo/communities">
            <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white px-8 h-14 text-lg">
              View the Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
